import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { RootState } from '../../store';
import { sendExploreMessage, summariseExplore, resetExplore } from '../../explore/exploreActions';
import { hydrateFromExplore } from '../../insight/insightActions';
import { TopInsight, ChatMessage } from '../../explore/exploreTypes';
import './ExplorePage.css';

type FormatKey = 'linkedin' | 'tweet' | 'thread' | 'newsletter' | 'script';
type AngleKey = 'thoughtful' | 'contrarian' | 'bold' | 'personal' | 'viral';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8085';

const TOPIC_CHIPS = [
  'Leadership', 'Productivity', 'Career', 'Strategy',
  'Founder Life', 'Marketing', 'Creativity', 'Culture',
];

const FORMATS: { label: string; key: FormatKey }[] = [
  { label: 'LinkedIn', key: 'linkedin' },
  { label: 'X Post', key: 'tweet' },
  { label: 'X Thread', key: 'thread' },
  { label: 'Email', key: 'newsletter' },
  { label: 'Video Script', key: 'script' },
];

const ANGLES: { label: string; key: AngleKey }[] = [
  { label: 'Thoughtful', key: 'thoughtful' },
  { label: 'Contrarian', key: 'contrarian' },
  { label: 'Bold', key: 'bold' },
  { label: 'Personal', key: 'personal' },
  { label: 'Viral', key: 'viral' },
];

const ExplorePage: React.FC = () => {
  const dispatch = useDispatch();
  const { messages, topInsights, sessionId, isLoading, isSummarising } = useSelector(
    (s: RootState) => s.Explore
  );

  const [inputText, setInputText] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [publishingInsight, setPublishingInsight] = useState<TopInsight & { pov?: { agree: string; disagree: string } } | null>(null);
  const [activeAngle, setActiveAngle] = useState<AngleKey>('thoughtful');
  const [activeFormat, setActiveFormat] = useState<FormatKey>('linkedin');
  const [generatedOutputs, setGeneratedOutputs] = useState<Partial<Record<AngleKey, Record<FormatKey, string>>>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [outputText, setOutputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [charCount, setCharCount] = useState('');
  const [copied, setCopied] = useState(false);
  const [summarisingIdx, setSummarisingSidx] = useState<number | null>(null);

  const chatMsgsRef = useRef<HTMLDivElement>(null);
  const publishSectionRef = useRef<HTMLDivElement>(null);
  const typingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll within the chat container — never the page
  useEffect(() => {
    const el = chatMsgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // When proposedInsight arrives from summarise saga, enrich and show publishing section
  const proposedInsight = useSelector((s: RootState) => s.Explore.proposedInsight);
  useEffect(() => {
    if (!proposedInsight) return;
    const enriched = { ...proposedInsight, score: 10 };
    setPublishingInsight(enriched as any);
    dispatch(hydrateFromExplore({
      distilledInsight: proposedInsight.distilledInsight,
      topic: proposedInsight.topic,
      toneTags: proposedInsight.toneTags,
      hook: proposedInsight.hook,
      rawThought: messages.find(m => m.role === 'user')?.content ?? '',
    }));
    setActiveAngle('thoughtful');
    setActiveFormat('linkedin');
    setGeneratedOutputs({});
    setOutputText('');
    setSummarisingSidx(null);
    setTimeout(() => {
      publishSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    triggerGenerate('thoughtful', proposedInsight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedInsight]);

  useEffect(() => {
    return () => {
      if (typingRef.current) clearInterval(typingRef.current);
    };
  }, []);

  const typeOut = (text: string) => {
    if (typingRef.current) clearInterval(typingRef.current);
    setOutputText('');
    setIsTyping(true);
    setCharCount('');
    const chars = text.split('');
    let i = 0;
    const speed = Math.max(7, Math.min(16, 1100 / chars.length));
    typingRef.current = setInterval(() => {
      i++;
      setOutputText(chars.slice(0, i).join(''));
      if (i >= chars.length) {
        clearInterval(typingRef.current!);
        setIsTyping(false);
        setCharCount(`${chars.length} chars`);
      }
    }, speed);
  };

  const triggerGenerate = async (angle: AngleKey, insight?: any) => {
    const src = insight ?? publishingInsight;
    if (!src) return;
    if (generatedOutputs[angle]) {
      typeOut(generatedOutputs[angle]![activeFormat] ?? '');
      return;
    }
    setIsGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/generate`,
        { distilledInsight: src.distilledInsight, topic: src.topic, angle },
        { withCredentials: true }
      );
      const outputs: Record<FormatKey, string> = res.data.outputs;
      setGeneratedOutputs(prev => ({ ...prev, [angle]: outputs }));
      setIsGenerating(false);
      typeOut(outputs[activeFormat] ?? '');
    } catch {
      setIsGenerating(false);
    }
  };

  const handleSend = (text?: string) => {
    const msg = (text ?? inputText).trim();
    if (!msg || isLoading) return;
    if (!hasStarted) setHasStarted(true);
    dispatch(sendExploreMessage(msg, messages, sessionId));
    setInputText('');
  };

  const handleTopicChip = (topic: string) => {
    const msg = `I want to explore my thoughts on ${topic}`;
    setInputText(msg);
    handleSend(msg);
  };

  const handlePublish = (insight: TopInsight, idx: number) => {
    if (!sessionId) return;
    setSummarisingSidx(idx);
    dispatch(summariseExplore(sessionId, insight.distilledInsight, messages));
  };

  const handleAngle = async (key: AngleKey) => {
    setActiveAngle(key);
    if (generatedOutputs[key]) {
      typeOut(generatedOutputs[key]![activeFormat] ?? '');
      return;
    }
    await triggerGenerate(key);
  };

  const handleFormat = (key: FormatKey) => {
    setActiveFormat(key);
    const outputs = generatedOutputs[activeAngle];
    if (outputs) typeOut(outputs[key] ?? '');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText).catch(() => { });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReset = () => {
    dispatch(resetExplore());
    setHasStarted(false);
    setInputText('');
    setPublishingInsight(null);
    setOutputText('');
    setGeneratedOutputs({});
    setSummarisingSidx(null);
  };

  const renderOutput = () => {
    if (activeFormat === 'newsletter') {
      const bodyStart = outputText.indexOf('\n\n');
      const hasBody = bodyStart >= 0;
      const subjectRaw = outputText.match(/^Subject: ([^\n]*)/);
      const subjectVal = subjectRaw ? subjectRaw[1] : outputText.startsWith('Subject') ? outputText.slice(9) : outputText;
      const bodyText = hasBody ? outputText.slice(bodyStart + 2) : '';
      return (
        <div className="ep-email-wrapper">
          <div className="ep-email-header">
            <div className="ep-email-row"><span className="ep-email-label">From</span><span className="ep-email-from">you@distill.app</span></div>
            <div className="ep-email-row"><span className="ep-email-label">Subject</span><span className={`ep-email-subject${!hasBody && isTyping ? ' typing' : ''}`}>{subjectVal}</span></div>
          </div>
          <div className={`ep-email-body${hasBody && isTyping ? ' typing' : ''}`}>{bodyText}</div>
        </div>
      );
    }
    return <div className={`ep-out-text${isTyping ? ' typing' : ''}`}>{outputText}</div>;
  };

  return (
    <div className="ep-page">
      {/* Nav */}
      <nav className="ep-nav">
        <a href="/" className="ep-nav-logo">distill<sup>beta</sup></a>
        {hasStarted && (
          <button className="ep-nav-reset" onClick={handleReset}>
            ← Start over
          </button>
        )}
      </nav>

      {/* Entry — shown before chat starts */}
      {!hasStarted && (
        <section className="ep-entry">
          <h1 className="ep-entry-h">What's on your mind?</h1>
          <p className="ep-entry-sub">Pick a topic or type your own thought — our coach will help you find the insight hiding in it.</p>
          <div className="ep-topic-chips">
            {TOPIC_CHIPS.map(t => (
              <button key={t} className="ep-topic-chip" onClick={() => handleTopicChip(t)}>{t}</button>
            ))}
          </div>
          <div className="ep-entry-input-row">
            <textarea
              ref={inputRef}
              className="ep-input"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. I've been thinking about why most meetings are a waste of time…"
              rows={3}
            />
            <button className="ep-send-btn" onClick={() => handleSend()} disabled={!inputText.trim()}>
              Start →
            </button>
          </div>
        </section>
      )}

      {/* Main content — chat + insights side by side */}
      {hasStarted && (
        <div className="ep-main">
          {/* Chat column */}
          <div className="ep-chat-col">
            <div className="ep-chat-messages" ref={chatMsgsRef}>
              {messages.map((msg: ChatMessage, i: number) => (
                <div key={i} className={`ep-bubble ep-bubble--${msg.role}`}>
                  {msg.role === 'assistant' && <div className="ep-bot-dot" />}
                  <div className="ep-bubble-text">{msg.content}</div>
                </div>
              ))}
              {isLoading && (
                <div className="ep-bubble ep-bubble--assistant">
                  <div className="ep-bot-dot" />
                  <div className="ep-typing-dots"><span /><span /><span /></div>
                </div>
              )}
            </div>

            <div className="ep-chat-input-row">
              <textarea
                className="ep-input ep-input--chat"
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Reply…"
                rows={2}
                disabled={isLoading}
              />
              <button
                className="ep-send-btn"
                onClick={() => handleSend()}
                disabled={!inputText.trim() || isLoading}
              >
                {isLoading ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'ep-spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Insights column */}
          <div className="ep-insights-col">
            <div className="ep-insights-header">
              <span className="ep-insights-label">✦ Insights forming</span>
              {topInsights.length === 0 && (
                <span className="ep-insights-hint">Keep sharing — insights will surface here</span>
              )}
            </div>

            {topInsights.map((ins: TopInsight, idx: number) => (
              <div key={idx} className="ep-insight-card">
                <div className="ep-ic-score">{ins.score}/10</div>
                <div className="ep-ic-text">"{ins.distilledInsight}"</div>
                <div className="ep-ic-meta">
                  <span className="ep-ic-topic">{ins.topic}</span>
                  {ins.toneTags.map(t => <span key={t} className="ep-ic-tag">{t}</span>)}
                </div>
                <button
                  className="ep-publish-btn"
                  onClick={() => handlePublish(ins, idx)}
                  disabled={isSummarising}
                >
                  {isSummarising && summarisingIdx === idx ? (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'ep-spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg> Preparing…</>
                  ) : 'Publish this →'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Publishing section */}
      {publishingInsight && (
        <div className="ep-publishing" ref={publishSectionRef}>
          <div className="ep-pub-insight-card">
            <div className="ep-pub-label">✦ Your insight</div>
            <div className="ep-pub-insight-text">"{publishingInsight.distilledInsight}"</div>
            <div className="ep-pub-meta">
              <span className="ep-ic-topic">{publishingInsight.topic}</span>
              {publishingInsight.toneTags.map(t => <span key={t} className="ep-ic-tag">{t}</span>)}
            </div>
            {publishingInsight.pov && (
              <div className="ep-pov-row">
                <div className="ep-pov-card ep-pov--agree">
                  <div className="ep-pov-label">Similar take</div>
                  <div className="ep-pov-text">{publishingInsight.pov.agree}</div>
                </div>
                <div className="ep-pov-card ep-pov--disagree">
                  <div className="ep-pov-label">Opposing view</div>
                  <div className="ep-pov-text">{publishingInsight.pov.disagree}</div>
                </div>
              </div>
            )}
          </div>

          <div className="ep-angle-row">
            {ANGLES.map(a => (
              <button
                key={a.key}
                className={`ep-angle-btn${activeAngle === a.key ? ' active' : ''}`}
                onClick={() => handleAngle(a.key)}
                disabled={isGenerating}
              >
                {a.label}
                {generatedOutputs[a.key] && a.key !== activeAngle && (
                  <span className="ep-angle-check">✓</span>
                )}
              </button>
            ))}
          </div>

          {isGenerating && (
            <div className="ep-generating">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'ep-spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
              Generating {activeAngle} takes…
            </div>
          )}

          {!isGenerating && outputText && (
            <>
              <div className="ep-format-tabs">
                {FORMATS.map(f => (
                  <button
                    key={f.key}
                    className={`ep-ftab${activeFormat === f.key ? ' active' : ''}`}
                    onClick={() => handleFormat(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="ep-out-box">
                {renderOutput()}
                <div className="ep-copy-row">
                  <span className="ep-char-count">{charCount}</span>
                  <button
                    className={`ep-copy-btn${copied ? ' copied' : ''}`}
                    onClick={handleCopy}
                    disabled={isTyping}
                  >
                    {copied ? '✓ Copied!' : (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
